import { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { CheckCircle2, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { OAuthButtons } from '@/features/oauthSso';
import { config } from '@/config';
import { CredentialsSignInForm } from '@/features/auth/CredentialsSignInForm';
import { accountCameFromLink, getActiveAccountId } from '@/lib/activeAccount';
import { useHeldAccounts } from '@/hooks/useHeldAccounts';

/**
 * SignIn — first user-visible page on the new stack. Migrated from
 * Bulma to Tailwind + shadcn. Renders without AppShell (App.tsx
 * skips the shell for unauthenticated users per Phase 1).
 *
 * Layout: a centered card on a bg-background canvas. The card stays
 * narrow (max-w-sm) so input fields read at a comfortable line
 * length even on wide displays.
 *
 * Behavior:
 *   • POST email/password to /api/auth/login → invalidate currentUser
 *     query → the AuthContext.Provider effect navigates to /home
 *   • OAuth/OIDC providers (Google, enterprise SSO, …) render via
 *     <OAuthButtons>, which redirects through the backend auth-code flow
 *   • On any auth failure, surface a "Invalid username or password"
 *     message (form-level; field-level errors are out of scope)
 */
/**
 * Only allow same-origin relative paths as a post-login destination, to
 * prevent an open redirect: a single leading slash, never a
 * protocol-relative "//host" or an absolute URL. Mirrors the backend
 * OAuth `return_to` guard.
 */
function safeReturnTo(raw: string | null): string {
  if (raw && raw.startsWith('/') && !raw.startsWith('//')) {
    return raw;
  }
  return '/home';
}

export default function SignIn() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get('returnTo'));
  // Set by `/signout` on a confirmed sign-out. A query param rather than component state
  // because getting here is a full page navigation, which is the point — it drops every
  // trace of the old session. Deliberately distinct from the session-expiry wording: one
  // is something the user did, the other is something that happened to them.
  const signedOut = searchParams.get('signedOut') === '1';
  // "Add another account" from the account menu (docs/MultiAccountSessions.md §5): the same
  // sign-in, which adds an account to the browser rather than replacing the one signed in,
  // so a signed-in user is not bounced away from it.
  const adding = searchParams.get('add') === '1';
  // This tab was opened for an account this browser isn't signed in to (a link's account hint).
  const forLinkedAccount = !user && accountCameFromLink();
  // One the browser was signed in to, and still lists as signed out, can be named. Any other
  // stays an opaque id: the link carries no address on purpose (docs/MultiAccountSessions.md §6).
  const { data: held } = useHeldAccounts(forLinkedAccount);
  const linkedAccount = forLinkedAccount ? held?.find((account) => account.id === getActiveAccountId()) : undefined;
  const initialEmail = searchParams.get('email') ?? linkedAccount?.email;

  // Once authenticated (fresh login, or an already-valid session cookie),
  // bounce to the requested destination — defaults to /home. This is what
  // returns an existing user to e.g. an invitation page so they can accept.
  useEffect(() => {
    if (user && !adding) {
      void navigate(returnTo);
    }
  }, [user, adding, navigate, returnTo]);


  return (
    <main className="min-h-svh flex flex-col items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-sm">
        {/* Brand mark above the card — same N tile as the SideNav so
            the visual lineage is obvious before the user gets in. */}
        <div className="flex flex-col items-center mb-6">
          <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center text-primary-foreground font-bold mb-3">
            {config.appName.charAt(0)}
          </div>
          <h1 className="text-xl font-semibold tracking-tight">
            {adding ? 'Add another account' : `Sign in to ${config.appName}`}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {adding
              ? 'You stay signed in to the accounts you have. Switch between them from your account menu.'
              : 'Manage your network from anywhere.'}
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg shadow-sm">
          <CredentialsSignInForm
            // The linked account's address arrives after the first render; the form reads
            // its initial email once.
            key={initialEmail ?? ''}
            initialEmail={initialEmail}
            intro={
              signedOut ? (
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>You&rsquo;ve been signed out.</AlertDescription>
                </Alert>
              ) : forLinkedAccount ? (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    {linkedAccount
                      ? `This link is for ${linkedAccount.email}, which is signed out in this browser. Sign in to open it.`
                      : 'This link is for an account that isn’t signed in here. Sign in to open it.'}
                  </AlertDescription>
                </Alert>
              ) : null
            }
          />

          {/* OAuth/OIDC providers (Google, Microsoft, enterprise SSO, …).
              Renders nothing — including the divider — when none are
              configured/enabled, so the card is just the password form. */}
          <div className="px-6 pb-6">
            <OAuthButtons returnTo={returnTo} withDivider />
          </div>
        </div>

        {adding ? (
          <p className="text-sm text-muted-foreground text-center mt-5">
            <Link to={returnTo} className="text-primary hover:underline font-medium">
              Cancel
            </Link>
          </p>
        ) : (
          <p className="text-sm text-muted-foreground text-center mt-5">
            Don't have an account?{' '}
            <Link to="/account/signup" className="text-primary hover:underline font-medium">
              Sign up
            </Link>
          </p>
        )}
      </div>
    </main>
  );
}
